import React from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../context/SchoolContext';
import { useAuth } from '../context/AuthContext';
import type { SchoolCode } from '../types';

interface SchoolSwitcherProps {
  className?: string;
  compact?: boolean;
}

export const SchoolSwitcher: React.FC<SchoolSwitcherProps> = ({ className = '', compact = false }) => {
  const { currentSchool, setCurrentSchool, getSchoolInfo, canSwitchSchool } = useSchool();
  const { user } = useAuth();
  const showUnlockLink = user?.role === 'admin' && !canSwitchSchool;

  if (!canSwitchSchool) {
    // Non-admin users just see their school name
    const info = getSchoolInfo(currentSchool);
    return (
      <div className={`school-badge ${className}`} style={styles.badge}>
        <span style={styles.icon}>
          <img 
            src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'} 
            alt={`${currentSchool} Logo`}
            style={{ width: '20px', height: '20px', objectFit: 'contain' }}
          />
        </span>
        {!compact && <span style={styles.name}>{info.shortName}</span>}
        {showUnlockLink && !compact && (
          <Link to="/admin/cross-school" style={{ marginLeft: 10, fontSize: 12, color: '#2563eb' }}>
            Other school…
          </Link>
        )}
      </div>
    );
  }

  const handleSchoolChange = (school: SchoolCode) => {
    void setCurrentSchool(school);
  };

  const kbaInfo = getSchoolInfo('KBA');
  const tlcInfo = getSchoolInfo('TLC');

  return (
    <div className={`school-switcher ${className}`} style={styles.container}>
      <button
        onClick={() => handleSchoolChange('KBA')}
        style={{
          ...styles.switchButton,
          ...(currentSchool === 'KBA' ? styles.activeButton : styles.inactiveButton),
          ...(currentSchool === 'KBA' ? styles.kbaActive : {}),
        }}
        title={`${kbaInfo.name} - ${kbaInfo.description}`}
      >
        <span style={styles.switchIcon}>
          <img 
            src="/kba logo.PNG" 
            alt="KBA Logo"
            style={{ width: '20px', height: '20px', objectFit: 'contain', verticalAlign: 'middle' }}
          />
        </span>
        {!compact && <span style={styles.switchText}>{kbaInfo.shortName}</span>}
      </button>
      
      <button
        onClick={() => handleSchoolChange('TLC')}
        style={{
          ...styles.switchButton,
          ...(currentSchool === 'TLC' ? styles.activeButton : styles.inactiveButton),
          ...(currentSchool === 'TLC' ? styles.tlcActive : {}),
        }}
        title={`${tlcInfo.name} - ${tlcInfo.description}`}
      >
        <span style={styles.switchIcon}>
          <img 
            src="/tlc logo.jpg" 
            alt="TLC Logo"
            style={{ width: '20px', height: '20px', objectFit: 'contain', verticalAlign: 'middle' }}
          />
        </span>
        {!compact && <span style={styles.switchText}>{tlcInfo.shortName}</span>}
      </button>
    </div>
  );
};

// Larger dropdown version for admin dashboard
export const SchoolSelector: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { currentSchool, setCurrentSchool, getSchoolInfo, canSwitchSchool } = useSchool();
  const { user } = useAuth();
  const showUnlockLink = user?.role === 'admin' && !canSwitchSchool;

  if (!canSwitchSchool) {
    const info = getSchoolInfo(currentSchool);
    return (
      <div className={className} style={styles.selectorContainer}>
        <div style={styles.currentSchool}>
          <span style={styles.selectorIcon}>
            <img 
              src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'} 
              alt={`${currentSchool} Logo`}
              style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            />
          </span>
          <div style={styles.schoolDetails}>
            <span style={styles.schoolName}>{info.name}</span>
            <span style={styles.schoolType}>{info.description}</span>
            {showUnlockLink && (
              <Link to="/admin/cross-school" style={{ display: 'block', marginTop: 8, fontSize: 13, color: '#2563eb' }}>
                Unlock cross-school session…
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={styles.selectorContainer}>
      <div style={styles.selectorLabel}>Managing School:</div>
      <div style={styles.selectorButtons}>
        <button
          onClick={() => void setCurrentSchool('KBA')}
          style={{
            ...styles.selectorButton,
            ...(currentSchool === 'KBA' ? styles.selectorActive : styles.selectorInactive),
            ...(currentSchool === 'KBA' ? { background: '#1e3a5f' } : {}),
          }}
        >
          <span style={styles.selectorButtonIcon}>
            <img 
              src="/kba logo.PNG" 
              alt="KBA Logo"
              style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            />
          </span>
          <div style={styles.selectorButtonDetails}>
            <span style={styles.selectorButtonName}>Kingdom Builder Academy</span>
            <span style={styles.selectorButtonType}>Secondary School</span>
          </div>
          {currentSchool === 'KBA' && <span style={styles.checkmark}>✓</span>}
        </button>
        
        <button
          onClick={() => void setCurrentSchool('TLC')}
          style={{
            ...styles.selectorButton,
            ...(currentSchool === 'TLC' ? styles.selectorActive : styles.selectorInactive),
            ...(currentSchool === 'TLC' ? { background: '#15803d' } : {}),
          }}
        >
          <span style={styles.selectorButtonIcon}>
            <img 
              src="/tlc logo.jpg" 
              alt="TLC Logo"
              style={{ width: '32px', height: '32px', objectFit: 'contain' }}
            />
          </span>
          <div style={styles.selectorButtonDetails}>
            <span style={styles.selectorButtonName}>The Learning Center</span>
            <span style={styles.selectorButtonType}>Primary & Nursery School</span>
          </div>
          {currentSchool === 'TLC' && <span style={styles.checkmark}>✓</span>}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '4px',
    padding: '3px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
  },
  switchButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
    fontWeight: 500,
  },
  activeButton: {
    color: '#fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  inactiveButton: {
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#334155',
    border: '1px solid rgba(100, 116, 139, 0.2)',
  },
  kbaActive: {
    background: '#1e3a5f',
  },
  tlcActive: {
    background: '#15803d',
  },
  switchIcon: {
    fontSize: '14px',
  },
  switchText: {
    letterSpacing: '0.5px',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 500,
  },
  icon: {
    fontSize: '14px',
  },
  name: {
    letterSpacing: '0.5px',
  },
  // Selector styles
  selectorContainer: {
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  selectorLabel: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: 600,
  },
  selectorButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  selectorButton: {
    flex: '1 1 200px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left' as const,
    position: 'relative' as const,
  },
  selectorActive: {
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
  },
  selectorInactive: {
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#334155',
    border: '1px solid rgba(100, 116, 139, 0.2)',
  },
  selectorButtonIcon: {
    fontSize: '28px',
  },
  selectorButtonDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  selectorButtonName: {
    fontSize: '14px',
    fontWeight: 600,
  },
  selectorButtonType: {
    fontSize: '11px',
    opacity: 0.8,
  },
  checkmark: {
    position: 'absolute' as const,
    top: '8px',
    right: '10px',
    fontSize: '16px',
    color: '#4ade80',
  },
  currentSchool: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectorIcon: {
    fontSize: '28px',
  },
  schoolDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  schoolName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
  },
  schoolType: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.6)',
  },
};

export default SchoolSwitcher;

